import { addDays } from 'date-fns'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  type CalendarWeek,
  clampDateToGrid,
  DAYS_IN_WEEK,
  makeDayKey,
} from '../monthGrid'

const KEY_OFFSETS: Record<string, number> = {
  ArrowDown: DAYS_IN_WEEK,
  ArrowLeft: -1,
  ArrowRight: 1,
  ArrowUp: -DAYS_IN_WEEK,
}

export type UseDayFocusParams = {
  onDayOpen?: (date: Date) => void
  onEscape?: () => void
  weeks: CalendarWeek[]
}

export type UseDayFocusValue = {
  /** Day that holds the grid's single tab stop. */
  focusedKey: string
  handleKeyDown: React.KeyboardEventHandler<HTMLDivElement>
  rootRef: React.RefObject<HTMLDivElement>
}

/**
 * Moves a single tab stop around the day cells with the arrow keys, opens a day
 * with Enter, and passes Escape up.
 *
 * Arrows stop at the edges of the rendered grid rather than paging to the next
 * month: paging belongs to the toolbar, and a keyboard user who has walked off
 * the edge should feel the wall instead of losing the month they were reading.
 */
export const useDayFocus = ({
  onDayOpen,
  onEscape,
  weeks,
}: UseDayFocusParams): UseDayFocusValue => {
  const rootRef = useRef<HTMLDivElement>(null)
  const shouldRestoreFocus = useRef(false)
  const [requestedKey, setRequestedKey] = useState<string | null>(null)

  const daysByKey = useMemo(
    () =>
      new Map(weeks.flatMap((week) => week.days).map((day) => [day.key, day])),
    [weeks]
  )

  const defaultKey = useMemo(() => {
    const days = weeks.flatMap((week) => week.days)
    return (
      days.find((day) => day.isToday) ??
      days.find((day) => day.isCurrentMonth) ??
      days[0]
    ).key
  }, [weeks])

  // A key from the month we just left is no longer in the grid, so paging
  // resets the tab stop without any extra bookkeeping.
  const focusedKey =
    requestedKey && daysByKey.has(requestedKey) ? requestedKey : defaultKey

  useEffect(() => {
    if (!shouldRestoreFocus.current) return
    shouldRestoreFocus.current = false
    rootRef.current
      ?.querySelector<HTMLElement>(`[data-date="${focusedKey}"]`)
      ?.focus()
  }, [focusedKey])

  // Every day cell is handed this, and a cell is drawn again only when what
  // it is handed changes.
  const handleKeyDown = useCallback<React.KeyboardEventHandler<HTMLDivElement>>(
    (event) => {
      // Keys from a menu drawn in a portal still bubble here through React, and
      // Escape in that menu only shuts the menu.
      if (
        !(event.target instanceof Node) ||
        !rootRef.current?.contains(event.target)
      ) {
        return
      }

      if (event.key === 'Escape') {
        onEscape?.()
        return
      }

      // Every other key belongs to the focused day cell. A bar or chip inside a
      // cell keeps its own Enter and arrow keys.
      if (
        !(event.target instanceof Element) ||
        event.target.getAttribute('data-date') !== focusedKey
      ) {
        return
      }

      const focusedDate = daysByKey.get(focusedKey)?.date
      if (!focusedDate) return

      if (event.key === 'Enter') {
        event.preventDefault()
        onDayOpen?.(focusedDate)
        return
      }

      const offset = KEY_OFFSETS[event.key]
      if (offset === undefined) return

      event.preventDefault()

      const nextKey = makeDayKey(
        clampDateToGrid(addDays(focusedDate, offset), weeks)
      )
      // An arrow at the edge of the grid moves nothing, so nothing is drawn
      // again. Asking for focus anyway would leave the request standing until
      // some later change to the tab stop, which would then pull focus back into
      // the grid from wherever it had gone.
      if (nextKey === focusedKey) return

      shouldRestoreFocus.current = true
      setRequestedKey(nextKey)
    },
    [daysByKey, focusedKey, onDayOpen, onEscape, weeks]
  )

  return { focusedKey, handleKeyDown, rootRef }
}
